import * as React from 'react';
import { useState } from 'react';
import {
  useRecordContext,
  useGetOne,
  useGetList,
  useUpdate,
  useNotify,
  useRefresh,
  Identifier,
  RaRecord,
} from 'react-admin';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/avatar';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';

interface Profile {
  id: number | string;
  name?: string;
  email?: string;
  avatar_url?: string;
  active?: boolean;
}

function OwnerAvatar({
  profile,
  className,
}: {
  profile?: Profile | null;
  className?: string;
}) {
  const initial = (profile?.name || '?').trim().charAt(0).toUpperCase();
  return (
    <Avatar
      size="sm"
      className={cn(
        'h-6 w-6 text-[10px]',
        !profile && 'bg-muted text-muted-foreground',
        className,
      )}
    >
      {profile?.avatar_url ? (
        <img
          src={`${profile.avatar_url}?size=24x24`}
          referrerPolicy="no-referrer"
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        initial
      )}
    </Avatar>
  );
}

type OwnerNoun = 'case' | 'lead';

interface OwnedRecord extends RaRecord {
  owner_id?: Identifier | null;
}

/**
 * Owner control for a case or a lead — the same chip and picker on both, so a
 * case and a lead are reassigned with one control everywhere. shadcn
 * command-dialog picker: shows the current owner (avatar + name); clicking
 * opens a searchable staff list to reassign `<resource>.owner_id`.
 *
 * On a lead the write also stamps owner_assigned_at / owner_source='manual'
 * (a manual reassign is the one override of the automatic rules).
 */
export function OwnerSelector({
  resource = 'funerals',
  noun = 'case',
  compact = false,
}: {
  resource?: 'funerals' | 'leads';
  noun?: OwnerNoun;
  /** Avatar + first name, no "Owner" label: for tight spots like the inbox dock. */
  compact?: boolean;
}) {
  const record = useRecordContext<OwnedRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [update, { isLoading: saving }] = useUpdate();

  const ownerId = record?.owner_id;
  const { data: owner } = useGetOne<Profile>(
    'profiles',
    { id: ownerId as Profile['id'] },
    { enabled: !!ownerId },
  );

  const { data: staff = [], isLoading } = useGetList<Profile>(
    'profiles',
    {
      filter: { active: true },
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 200 },
    },
    { enabled: open },
  );

  if (!record) return null;

  const assign = (profile: Profile) => {
    const data =
      resource === 'leads'
        ? {
            owner_id: profile.id,
            owner_assigned_at: new Date().toISOString(),
            owner_source: 'manual',
          }
        : { owner_id: profile.id };
    void update(
      resource,
      { id: record.id, data, previousData: record },
      {
        onSuccess: () => {
          notify(`${noun === 'lead' ? 'Lead' : 'Case'} owner updated`, {
            type: 'success',
          });
          setOpen(false);
          refresh();
        },
        onError: (err: unknown) =>
          notify(
            `Failed to update owner: ${
              err instanceof Error ? err.message : String(err)
            }`,
            { type: 'error' },
          ),
      },
    );
  };

  const label = owner?.name || (ownerId ? '…' : 'Unassigned');

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title={`${label} · change ${noun} owner`}
          className="cleo-ui flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card py-0.5 pl-0.5 pr-2 text-xs transition-colors hover:bg-muted"
        >
          <OwnerAvatar profile={owner} className="h-5 w-5" />
          <span className="max-w-[80px] truncate font-medium text-foreground">
            {owner?.name ? owner.name.trim().split(/\s+/)[0] : label}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          title={`Change ${noun} owner`}
          className="cleo-ui flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1 text-sm transition-colors hover:bg-muted"
        >
          <span className="hidden text-xs text-muted-foreground sm:inline">Owner</span>
          <OwnerAvatar profile={owner} />
          <span className="max-w-[160px] truncate font-medium text-foreground">
            {label}
          </span>
        </button>
      )}

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Assign owner…" disabled={saving} />
        <CommandList>
          <CommandEmpty>{isLoading ? 'Loading…' : 'No staff found.'}</CommandEmpty>
          <CommandGroup heading="Team">
            {staff.map((p) => (
              <CommandItem
                key={p.id}
                value={p.name || p.email || String(p.id)}
                onSelect={() => assign(p)}
              >
                <OwnerAvatar profile={p} />
                <span className="flex-1 truncate">{p.name || p.email || p.id}</span>
                {String(p.id) === String(ownerId) && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}

export default OwnerSelector;
